import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import dayjs from 'lib/dayjs';
import { useCurrentConversationValue } from 'hooks/business/conversation';
import { TradeDetailBlock, TradeDetailBlockId } from './tradeDetailBlockTypes';
import { useTradeAmountBlocks } from './useTradeAmountBlocks';
import { useTradeLogisticsBlocks } from './useTradeLogisticsBlocks';

export type {
  TradeDetailAmountBlock,
  TradeDetailAmountVariant,
  TradeDetailBlock,
  TradeDetailBlockId,
  TradeDetailLogisticsBlock,
  TradeDetailTextBlock,
} from './tradeDetailBlockTypes';

export interface UseTradeDetailBusinessParams {
  trade: Conversation.Trade.TradeWithLogistics;
  sendable?: boolean;
}

export interface UseTradeDetailBusinessResult {
  blocks: TradeDetailBlock[];
  conversation?: Conversation.Conversation;
  storeType?: Conversation.Trade.IStoreType;
}

const TRADE_STATUS_BLOCK_ORDER: Record<Conversation.Trade.Status, TradeDetailBlockId[]> = {
  WAITING_PAY: ['paymentAmount', 'incomeAmount', 'fulfillmentFlag'],
  WAITING_DELIVERY: [
    'paymentAmount',
    'incomeAmount',
    'fulfillmentFlag',
    'logisticsInfo',
    'thirdLogisticsInfo',
    'shipByDate',
    'paymentTime',
  ],
  WAITING_SIGN: [
    'paymentAmount',
    'incomeAmount',
    'fulfillmentFlag',
    'logisticsInfo',
    'thirdLogisticsInfo',
    'latestLogisticsTime',
    'pickupDoneTime',
  ],
  TRADE_SUCCESS: [
    'paymentAmount',
    'incomeAmount',
    'fulfillmentFlag',
    'logisticsInfo',
    'thirdLogisticsInfo',
    'completeTime',
  ],
  CANCEL: [
    'paymentAmount',
    'incomeAmount',
    'fulfillmentFlag',
    'logisticsInfo',
    'thirdLogisticsInfo',
  ],
  IN_CANCELING: [
    'paymentAmount',
    'incomeAmount',
    'fulfillmentFlag',
    'logisticsInfo',
    'thirdLogisticsInfo',
  ],
  REFUND: [
    'paymentAmount',
    'incomeAmount',
    'fulfillmentFlag',
    'logisticsInfo',
    'thirdLogisticsInfo',
    'latestLogisticsTime',
    'pickupDoneTime',
  ],
  MULTIPLE_STATUS: [],
  UNKNOWN_ORDER_STATUS: [],
};

const formatTimestamp = (timestamp: string | number) =>
  dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm:ss');

export const useTradeDetailBasicInfoBlocks = (trade: Conversation.Trade.TradeWithLogistics) => {
  const { t } = useTranslate();
  const conversation = useCurrentConversationValue();
  const channel = conversation?.channel;

  const blocks = useMemo(() => {
    const { modifiedTimeInMs, payTimeInMs, shipByDate, pickupDoneTime, fulfillmentFlag } = trade;

    const blockMap: Partial<Record<TradeDetailBlockId, TradeDetailBlock>> = {
      completeTime: {
        id: 'completeTime',
        type: 'text',
        label: t('domain.order.field.completedTime'),
        value: formatTimestamp(modifiedTimeInMs),
      },
    };

    if (channel === 'SHOPEE') {
      blockMap.fulfillmentFlag = {
        id: 'fulfillmentFlag',
        type: 'text',
        label: t('domain.order.field.shippingMethod'),
        value: t({
          key: `fulfillmentFlagValues.${fulfillmentFlag}`,
          defaultValue: '-',
        }),
      };
    }

    const payTimestamp = Number(payTimeInMs);
    if (payTimestamp) {
      blockMap.paymentTime = {
        id: 'paymentTime',
        type: 'text',
        label: t('domain.order.field.paymentTime'),
        value: formatTimestamp(payTimestamp),
      };
    }

    const shipByDateMs = Number(shipByDate);
    if (shipByDateMs) {
      blockMap.shipByDate = {
        id: 'shipByDate',
        type: 'text',
        label: t('domain.order.field.shipmentDate'),
        value: formatTimestamp(shipByDateMs),
      };
    }

    const pickupDoneTimeMs = Number(pickupDoneTime);
    if (pickupDoneTimeMs) {
      blockMap.pickupDoneTime = {
        id: 'pickupDoneTime',
        type: 'text',
        label: t('domain.order.field.deliveryTime'),
        value: formatTimestamp(pickupDoneTimeMs),
      };
    }

    return blockMap;
  }, [channel, t, trade]);

  return blocks;
};

export const getTradeDetailBlocks = (
  tradeStatus: Conversation.Trade.Status,
  blockMap: Partial<Record<TradeDetailBlockId, TradeDetailBlock>>,
) => {
  const orderedIds = TRADE_STATUS_BLOCK_ORDER[tradeStatus];

  if (!orderedIds) {
    return [];
  }

  return orderedIds
    .map((id) => blockMap[id])
    .filter((block): block is TradeDetailBlock => Boolean(block));
};

export const useTradeDetailBusiness = (
  params: UseTradeDetailBusinessParams,
): UseTradeDetailBusinessResult => {
  const { trade, sendable = true } = params;

  const conversation = useCurrentConversationValue();
  const amountBlockMap = useTradeAmountBlocks(trade);
  const logisticsBlockMap = useTradeLogisticsBlocks(trade, { sendable });
  const basicInfoBlocks = useTradeDetailBasicInfoBlocks(trade);

  const blocks = useMemo(() => {
    const { tradeStatus } = trade;

    const blockMap = { ...basicInfoBlocks };

    Object.assign(blockMap, amountBlockMap);

    Object.assign(blockMap, logisticsBlockMap);

    return getTradeDetailBlocks(tradeStatus, blockMap);
  }, [trade, basicInfoBlocks, amountBlockMap, logisticsBlockMap]);

  return {
    blocks,
    conversation,
    storeType: conversation?.topBarInfo?.storeType,
  };
};
